import { CpgReport } from '@features/campaigns';
import { contentSlideHeader } from '@features/lci-report/utils';
import { buildStyledTable } from '@features/lci-report/utils/build-table';
import { Slide, TextProps } from '@features/powerpoint';
import { formatters, ValueInput } from '@utils/formattersV2';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { formatters as cpgFormatters } from '@features/campaigns/subdomains/cpg';

type MetricTextProps = {
  name: string;
  nameAdditionalText?: string;
  value: string;
  description?: string;
  textOptions?: TextProps['options'];
};

const metricText = (props: MetricTextProps): TextProps => {
  return {
    text: [
      {
        text: props.name,
        options: {
          color: unifiedPlatformColorPalette.inmarketGreen,
          fontSize: 12,
          bold: true,
        },
      },
      {
        text: ` ${props.nameAdditionalText ?? ''}`,
        options: {
          color: unifiedPlatformColorPalette.inmarketGreen,
          fontSize: 12,
          breakLine: true,
        },
      },
      {
        text: props.value,
        options: {
          fontSize: 20,
          bold: true,
          breakLine: true,
        },
      },
      ...(props.description
        ? [
            {
              text: props.description,
              options: {
                fontSize: 10,
                breakLine: true,
              },
            },
          ]
        : []),
    ],
    options: {
      h: 0.95,
      w: 3.35,
      x: 0.82,
      lineSpacingMultiple: 1.3,
      valign: 'top',
      fit: 'resize',
      ...props.textOptions,
    },
  };
};

type TableField = {
  columnLabel: string;
  getValue: (report: SalesLiftSummarySlideProps['report']) => string;
  key: keyof SalesLiftSummarySlideProps['report'];
};

const tableFields = [
  {
    columnLabel: 'Projected Incremental Sales',
    getValue: report =>
      formatters.currency(report.projectedIncrementalSales, {
        full: true,
        decimals: 0,
      }),
    key: 'projectedIncrementalSales',
  },
  {
    columnLabel: 'Total Projected Sales',
    getValue: report =>
      formatters.currency(report.totalProjectedSales, {
        full: true,
        decimals: 0,
      }),
    key: 'totalProjectedSales',
  },
  {
    columnLabel: 'Total Sales Rate',
    getValue: report => formatters.currency(report.totalSalesRate),
    key: 'totalSalesRate',
  },
  {
    columnLabel: 'Avg. Price per Transaction',
    getValue: report => formatters.currency(report.averagePricePerTransaction),
    key: 'averagePricePerTransaction',
  },
  {
    columnLabel: 'Penetration',
    getValue: report =>
      formatters.percentage(report.penetration, { decimals: 2 }),
    key: 'penetration',
  },
  {
    columnLabel: 'Avg. Purchase Frequency',
    getValue: report =>
      cpgFormatters.formatAveragePurchaseFrequency(
        report.averagePurchaseFrequency
      ),
    key: 'averagePurchaseFrequency',
  },
  {
    columnLabel: 'Avg. Total Basket Amount',
    getValue: report => formatters.currency(report.averageTotalBasketAmount),
    key: 'averageTotalBasketAmount',
  },
] as const satisfies readonly TableField[];

type SalesLiftSummarySlideProps = {
  slideMaster: string;
  report: Pick<
    CpgReport,
    | 'incrementalSalesLift'
    | 'incrementalSalesLiftConfidenceLevel'
    | 'projectedIncrementalSales'
    | 'totalProjectedSales'
    | 'totalSalesRate'
    | 'averagePricePerTransaction'
    | 'penetration'
    | 'averagePurchaseFrequency'
    | 'averageTotalBasketAmount'
  >;
  roas: ValueInput;
  iroas: ValueInput;
  excludedColumns: TableField['key'][];
};

export const salesLiftSummarySlide = (
  props: SalesLiftSummarySlideProps
): Slide => {
  const filteredTableFields = tableFields.filter(
    field => !props.excludedColumns.includes(field.key)
  );

  const summaryTable = buildStyledTable({
    columns: ['', 'Results'],
    rows: filteredTableFields.map(field => [
      field.columnLabel,
      field.getValue(props.report),
    ]),
    options: {
      h: 4,
      w: 4.04,
      x: 6.31,
      y: 1.71,
    },
  });

  return {
    slideMaster: props.slideMaster,
    tables: [summaryTable],
    texts: [
      contentSlideHeader('Sales Lift Summary'),
      metricText({
        name: 'Incremental Sales Lift',
        value: formatters.lift(props.report.incrementalSalesLift),
        description: `Confidence Level: ${formatters.confidenceLevel(props.report.incrementalSalesLiftConfidenceLevel, props.report.incrementalSalesLift)}`,
        textOptions: {
          y: 2.43,
        },
      }),
      metricText({
        name: 'IROAS',
        nameAdditionalText: '(Incremental Return on Ad Spend)',
        value: formatters.currency(props.iroas),
        textOptions: {
          y: 3.74,
        },
      }),
      metricText({
        name: 'ROAS',
        nameAdditionalText: '(Return on Ad Spend)',
        value: formatters.currency(props.roas),
        textOptions: {
          y: 4.82,
        },
      }),
    ],
  };
};
